/**
 * Human review queue for AI/rule-hidden comments.
 *
 * Reliability model:
 *  - Cursor-based pagination (created_time + _id), stable under removals.
 *  - Optimistic remove on action: row disappears immediately, rolled back on
 *    API failure with the comment re-inserted at its original index.
 *  - Auto-prefetch when local list drops below PREFETCH_THRESHOLD so the
 *    queue self-heals as the reviewer drains it.
 *  - Server actions are idempotent (atomic findOneAndUpdate guard), so racing
 *    tabs or socket-driven removals are safe.
 *  - Real-time: prepend on `new-comment` (hidden only), remove on
 *    `comment-reviewed` and `comment-updated` events from the page room.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  ArrowLeft,
  Loader2,
  Eye,
  ShieldCheck,
  Trash2,
  CheckSquare,
  Square,
  Inbox,
  Clock,
  AlertCircle,
  Filter,
  ExternalLink,
} from "lucide-react";
import { useApi, API_ENDPOINTS } from "../lib/api";
import {
  getSocket,
  joinPageRoom,
  leavePageRoom,
  onNewComment,
  offNewComment,
  onCommentUpdated,
  offCommentUpdated,
} from "../lib/socket";
import { useToast } from "../hooks/useToast";
import { useReviewCount } from "../hooks/useReviewCount";
import {
  useModerationRunner,
  RunItemResult,
} from "../components/ModerationToast";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";
import { CommentAvatar, getCommenterIdentity } from "../lib/commenterIdentity";

interface ReviewItem {
  commentId: string;
  postId: string;
  message: string;
  attachment?: {
    type?: string | null;
    url?: string | null;
    imageSrc?: string | null;
    title?: string | null;
  } | null;
  from: { id?: string; name?: string; picture?: string } | null;
  created_time: string;
  permalink_url: string | null;
  matchedRuleId: string | null;
  matchedRuleName: string | null;
  hideReason: string | null;
}

interface ReviewRule {
  ruleId: string;
  ruleName: string;
  count: number;
}

type ReviewAction = "approve" | "confirm-hide" | "delete" | "delete-and-block";

const PAGE_LIMIT = 50;
const PREFETCH_THRESHOLD = 30; // refill when local list drops below this


const ReviewQueue: React.FC = () => {
  const { pageId } = useParams<{ pageId: string }>();
  const api = useApi();
  const { toast } = useToast();

  const [items, setItems] = useState<ReviewItem[]>([]);
  const [rules, setRules] = useState<ReviewRule[]>([]);
  const [selectedRuleId, setSelectedRuleId] = useState<string | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [initialLoading, setInitialLoading] = useState(true);
  const [fetchingMore, setFetchingMore] = useState(false);
  const [actingIds, setActingIds] = useState<Set<string>>(new Set());
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  // Non-blocking toasts, so bulk buttons stay enabled; kept as a constant so
  // existing `disabled={bulkRunning}` chrome still type-checks.
  const bulkRunning = false;

  // Exact pending-review total for the headline + sidebar parity. The count
  // endpoint (socket-live) gives the unfiltered total; when a rule filter is
  // active, the queue's headline should reflect that rule's exact count instead.
  const totalReviewCount = useReviewCount(pageId);
  const exactTotal = selectedRuleId
    ? rules.find((r) => r.ruleId === selectedRuleId)?.count ?? items.length
    : totalReviewCount;

  // Non-blocking moderation progress toasts.
  const { run: runModeration } = useModerationRunner();

  // Refs to avoid stale closures inside socket handlers / async callbacks.
  const itemsRef = useRef<ReviewItem[]>([]);
  const cursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(true);
  const fetchingMoreRef = useRef(false);
  const selectedRuleIdRef = useRef<string | null>(null);

  useEffect(() => {
    itemsRef.current = items;
  }, [items]);
  useEffect(() => {
    cursorRef.current = cursor;
  }, [cursor]);
  useEffect(() => {
    hasMoreRef.current = hasMore;
  }, [hasMore]);
  useEffect(() => {
    fetchingMoreRef.current = fetchingMore;
  }, [fetchingMore]);
  useEffect(() => {
    selectedRuleIdRef.current = selectedRuleId;
  }, [selectedRuleId]);

  // -- Data loading -----------------------------------------------------------

  const fetchPage = useCallback(
    async (opts: { cursor?: string | null; ruleId?: string | null; replace?: boolean }) => {
      if (!pageId) return;
      const params = new URLSearchParams();
      params.set("limit", String(PAGE_LIMIT));
      if (opts.cursor) params.set("cursor", opts.cursor);
      if (opts.ruleId) params.set("ruleId", opts.ruleId);

      const url = `${API_ENDPOINTS.REVIEW_QUEUE(pageId)}?${params.toString()}`;
      const res = await api.get(url);
      const { items: fetched, hasMore: more, nextCursor } = res.data;
      setItems((prev) => (opts.replace ? fetched : [...prev, ...fetched]));
      setHasMore(more);
      setCursor(nextCursor);
    },
    [api, pageId]
  );

  const fetchRules = useCallback(async () => {
    if (!pageId) return;
    try {
      const res = await api.get(API_ENDPOINTS.REVIEW_QUEUE_RULES(pageId));
      setRules(res.data.rules || []);
    } catch (err) {
      console.error("[REVIEW_QUEUE] Failed to load rule filters", err);
    }
  }, [api, pageId]);

  // Initial load + reload on filter change.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setInitialLoading(true);
      try {
        await fetchPage({ cursor: null, ruleId: selectedRuleId, replace: true });
      } catch (err) {
        console.error("[REVIEW_QUEUE] Initial load failed", err);
        if (!cancelled) {
          toast({
            title: "Couldn't load review queue",
            description: "Please try again.",
            variant: "destructive",
          });
        }
      } finally {
        if (!cancelled) setInitialLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [pageId, selectedRuleId]);

  // Load rule list once per page.
  useEffect(() => {
    fetchRules();
  }, [pageId]);

  // Auto-prefetch: when local list shrinks below threshold and more is
  // available, fetch the next page silently.
  useEffect(() => {
    if (initialLoading) return;
    if (!hasMore || fetchingMore) return;
    if (items.length >= PREFETCH_THRESHOLD) return;
    if (!cursor) return;
    setFetchingMore(true);
    fetchPage({ cursor, ruleId: selectedRuleId, replace: false })
      .catch((err) => console.error("[REVIEW_QUEUE] Prefetch failed", err))
      .finally(() => setFetchingMore(false));
  }, [items.length, hasMore, fetchingMore, cursor, initialLoading, selectedRuleId, fetchPage]);

  // -- Real-time --------------------------------------------------------------

  useEffect(() => {
    if (!pageId) return;
    getSocket();
    joinPageRoom(pageId);

    const handleNewComment = (c: any) => {
      // Only care about hidden comments in this view.
      if (c?.status !== "hidden" && c?.moderationStatus !== "hidden") return;
      // If a rule filter is active, prepend only when it matches.
      const ruleFilter = selectedRuleIdRef.current;
      if (ruleFilter) {
        const matched = c?.matchedRuleId === ruleFilter ||
          (Array.isArray(c?.moderationResult?.matchedRules) &&
            c.moderationResult.matchedRules.includes(ruleFilter));
        if (!matched) return;
      }
      // Skip if already present.
      if (itemsRef.current.some((it) => it.commentId === (c.id || c.commentId))) return;
      const next: ReviewItem = {
        commentId: c.id || c.commentId,
        postId: c.postId || c.post_id || "",
        message: c.message || "",
        attachment: c.attachment || null,
        from: c.from || null,
        created_time: c.created_time || new Date().toISOString(),
        permalink_url: c.permalink_url || null,
        matchedRuleId: c.matchedRuleId || null,
        matchedRuleName: c.matchedRuleName || null,
        hideReason: c.hideReason || null,
      };
      setItems((prev) => [next, ...prev]);
    };

    const handleCommentUpdated = (u: any) => {
      // If a comment moved out of 'hidden' (or got reviewed elsewhere), drop it.
      if (!u?.id) return;
      if (u.status && u.status !== "hidden") {
        setItems((prev) => prev.filter((it) => it.commentId !== u.id));
      }
    };

    const handleCommentReviewed = (r: any) => {
      if (!r?.commentId) return;
      setItems((prev) => prev.filter((it) => it.commentId !== r.commentId));
      setSelectedIds((prev) => {
        if (!prev.has(r.commentId)) return prev;
        const next = new Set(prev);
        next.delete(r.commentId);
        return next;
      });
    };

    const socket = getSocket();
    onNewComment(handleNewComment);
    onCommentUpdated(handleCommentUpdated);
    socket.on("comment-reviewed", handleCommentReviewed);

    return () => {
      offNewComment(handleNewComment);
      offCommentUpdated(handleCommentUpdated);
      socket.off("comment-reviewed", handleCommentReviewed);
      leavePageRoom(pageId);
    };
  }, [pageId]);

  // -- Actions ----------------------------------------------------------------

  const runAction = useCallback(
    (commentId: string, action: ReviewAction) => {
      if (!pageId) return;
      const idx = itemsRef.current.findIndex((it) => it.commentId === commentId);
      if (idx === -1) return;
      const removed = itemsRef.current[idx];
      const handle = (removed as any)?.from?.name || "commenter";

      // Optimistic remove; the toast reports the real outcome.
      setActingIds((prev) => new Set(prev).add(commentId));
      setItems((prev) => prev.filter((it) => it.commentId !== commentId));
      setSelectedIds((prev) => {
        if (!prev.has(commentId)) return prev;
        const next = new Set(prev);
        next.delete(commentId);
        return next;
      });

      runModeration({
        pageId,
        action,
        items: [{ id: commentId, handle }],
        runItem: async (id): Promise<RunItemResult> => {
          try {
            await api.post(API_ENDPOINTS.REVIEW_ACTION(pageId, id, action), {});
            return { success: true };
          } catch (err: any) {
            // Roll back: re-insert at the same index.
            setItems((prev) => {
              const next = [...prev];
              next.splice(Math.min(idx, next.length), 0, removed);
              return next;
            });
            return {
              success: false,
              error:
                err?.response?.data?.error || err?.message || "Action failed",
              throttled:
                err?.response?.status === 429 ||
                !!err?.response?.data?.throttled,
              retryMinutes: err?.response?.data?.retryMinutes,
            };
          } finally {
            setActingIds((prev) => {
              const next = new Set(prev);
              next.delete(id);
              return next;
            });
          }
        },
      });
    },
    [api, pageId, runModeration]
  );

  const runBulk = useCallback(
    (action: ReviewAction) => {
      if (!pageId) return;
      const ids = Array.from(selectedIds);
      if (ids.length === 0) return;
      const snapshot = itemsRef.current.filter((it) =>
        selectedIds.has(it.commentId)
      );
      const toastItems = snapshot.map((s) => ({
        id: s.commentId,
        handle: (s as any)?.from?.name || "commenter",
      }));

      // Optimistic remove; failures are re-inserted per item by runItem.
      setItems((prev) => prev.filter((it) => !selectedIds.has(it.commentId)));
      setSelectedIds(new Set());

      runModeration({
        pageId,
        action,
        items: toastItems,
        runItem: async (id): Promise<RunItemResult> => {
          try {
            await api.post(API_ENDPOINTS.REVIEW_ACTION(pageId, id, action), {});
            return { success: true };
          } catch (err: any) {
            const s = snapshot.find((x) => x.commentId === id);
            if (s) setItems((prev) => [s, ...prev]);
            return {
              success: false,
              error:
                err?.response?.data?.error || err?.message || "Action failed",
              throttled:
                err?.response?.status === 429 ||
                !!err?.response?.data?.throttled,
              retryMinutes: err?.response?.data?.retryMinutes,
            };
          }
        },
      });
    },
    [api, pageId, selectedIds, runModeration]
  );

  const toggleSelect = (commentId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(commentId)) next.delete(commentId);
      else next.add(commentId);
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (prev.size === items.length && items.length > 0) return new Set();
      return new Set(items.map((it) => it.commentId));
    });
  };

  // -- Render -----------------------------------------------------------------

  const allSelected = items.length > 0 && selectedIds.size === items.length;
  const someSelected = selectedIds.size > 0;

  return (
    <div className="max-w-5xl mx-auto">
      {/* Header */}
      <div className="mb-6 flex items-start justify-between gap-4">
        <div>
          <Link
            to={pageId ? `/accounts/${pageId}/comments` : "/accounts"}
            className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-3"
          >
            <ArrowLeft className="h-4 w-4 mr-1" />
            Back to comments
          </Link>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Inbox className="h-6 w-6 text-blue-600" />
            Review Queue
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            {items.length === 0 ? (
              "No comments are waiting for your review right now."
            ) : (
              <>
                We caught {exactTotal} potentially harmful{" "}
                {exactTotal === 1 ? "comment" : "comments"} before your
                audience saw {exactTotal === 1 ? "it" : "them"}. Approve
                anything wrongly hidden, delete the spam.
              </>
            )}
          </p>
        </div>
        {/* Promoted pending count — the queue's headline metric. */}
        <div className="shrink-0 text-right">
          <div className="text-4xl font-bold leading-none text-amber-600 tabular-nums">
            {exactTotal}
          </div>
          <div className="mt-1 text-xs font-medium uppercase tracking-wide text-gray-500">
            pending review
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="mb-4 flex flex-wrap items-center gap-3 bg-white border border-gray-200 rounded-lg px-4 py-3">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Filter className="h-4 w-4 text-gray-400" />
          <span className="font-medium">Filter by rule:</span>
          <select
            value={selectedRuleId ?? ""}
            onChange={(e) => setSelectedRuleId(e.target.value || null)}
            className="border border-gray-200 rounded-md px-2 py-1 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/20"
          >
            <option value="">All rules</option>
            {rules.map((r) => (
              <option key={r.ruleId} value={r.ruleId}>
                {r.ruleName} ({r.count})
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1" />
        {someSelected && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm text-gray-600">
              {selectedIds.size} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              disabled={bulkRunning}
              onClick={() => runBulk("delete")}
              className="text-red-600 hover:text-red-700"
            >
              <Trash2 className="h-4 w-4 mr-1" /> Delete
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={bulkRunning}
              onClick={() => runBulk("confirm-hide")}
            >
              <ShieldCheck className="h-4 w-4 mr-1" /> Confirm hide
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={bulkRunning}
              onClick={() => runBulk("approve")}
              className="text-green-700 hover:text-green-800"
            >
              <Eye className="h-4 w-4 mr-1" /> Approve
            </Button>
            {bulkRunning && (
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            )}
          </div>
        )}
      </div>

      {/* List */}
      {initialLoading ? (
        <div className="flex justify-center py-16">
          <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState ruleFiltered={!!selectedRuleId} />
      ) : (
        <>
          <div className="mb-2 flex items-center gap-2 px-1">
            <button
              onClick={toggleSelectAll}
              className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700"
              type="button"
            >
              {allSelected ? (
                <CheckSquare className="h-4 w-4 text-blue-600" />
              ) : (
                <Square className="h-4 w-4" />
              )}
              {allSelected ? "Deselect all" : "Select all visible"}
            </button>
          </div>

          <div className="space-y-2">
            {items.map((it) => (
              <ReviewRow
                key={it.commentId}
                item={it}
                selected={selectedIds.has(it.commentId)}
                onToggle={() => toggleSelect(it.commentId)}
                onAction={(action) => runAction(it.commentId, action)}
                acting={actingIds.has(it.commentId)}
              />
            ))}
          </div>

          {fetchingMore && (
            <div className="flex justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
            </div>
          )}
          {!hasMore && items.length > 0 && (
            <div className="text-center text-xs text-gray-400 py-4">
              End of queue
            </div>
          )}
        </>
      )}
    </div>
  );
};

// -- Subcomponents ------------------------------------------------------------

const ReviewRow: React.FC<{
  item: ReviewItem;
  selected: boolean;
  onToggle: () => void;
  onAction: (action: ReviewAction) => void;
  acting: boolean;
}> = ({ item, selected, onToggle, onAction, acting }) => {
  const when = new Date(item.created_time);
  const ago = relativeTime(when);
  const identity = getCommenterIdentity(item);
  return (
    <div
      // 3px amber left accent so each row reads as "needs attention" at a glance.
      style={{ borderLeftWidth: 3, borderLeftColor: "#fbbf24" }}
      className={cn(
        "bg-white border rounded-lg p-4 flex gap-3 transition-colors",
        selected ? "border-blue-300 ring-1 ring-blue-200" : "border-gray-200",
        acting && "opacity-50"
      )}
    >
      <button
        type="button"
        onClick={onToggle}
        className="shrink-0 mt-0.5"
        aria-label={selected ? "Deselect" : "Select"}
      >
        {selected ? (
          <CheckSquare className="h-5 w-5 text-blue-600" />
        ) : (
          <Square className="h-5 w-5 text-gray-300 hover:text-gray-500" />
        )}
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <CommentAvatar info={identity} size={28} />
          <span
            className={cn(
              "font-medium truncate",
              identity.isAnonymous
                ? "text-gray-500 font-mono text-xs"
                : "text-gray-900"
            )}
            title={item.from?.id ? `ASID: ${item.from.id}` : undefined}
          >
            {identity.name}
          </span>
          <span className="text-xs text-gray-400 inline-flex items-center gap-1">
            <Clock className="h-3 w-3" /> {ago}
          </span>
          {item.permalink_url && (
            <a
              href={item.permalink_url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-blue-600 inline-flex items-center gap-1"
            >
              <ExternalLink className="h-3 w-3" /> View on{" "}
              {/instagram\.com/i.test(item.permalink_url) ? "Instagram" : "Facebook"}
            </a>
          )}
        </div>

        {item.message && (
          <p className="text-sm text-gray-800 mt-2 whitespace-pre-wrap break-words">
            {item.message}
          </p>
        )}
        {item.attachment?.imageSrc ? (
          <a
            href={item.attachment.url || item.attachment.imageSrc}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block"
          >
            <img
              src={item.attachment.imageSrc}
              alt={item.attachment.title || item.attachment.type || "comment media"}
              className="max-h-40 rounded border border-gray-200"
              loading="lazy"
            />
          </a>
        ) : (
          !item.message && (
            <p className="text-sm mt-2">
              <em className="text-gray-400">(no text — media/sticker only)</em>
            </p>
          )
        )}

        {(item.matchedRuleName || item.hideReason) && (
          <div className="mt-2 text-xs text-gray-500 inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 rounded px-2 py-1">
            <AlertCircle className="h-3 w-3" />
            Hidden by:{" "}
            <span className="font-semibold">
              {item.matchedRuleName || "(unnamed rule)"}
            </span>
            {item.hideReason && item.hideReason !== item.matchedRuleName && (
              <span className="text-amber-600">— {item.hideReason}</span>
            )}
          </div>
        )}

        {/* Task-queue actions: exactly two — Delete (left) and Approve (right), side by side. */}
        <div className="mt-3 flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={acting}
            onClick={() => onAction("delete")}
            className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
          >
            <Trash2 className="h-4 w-4 mr-1" /> Delete
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={acting}
            onClick={() => onAction("approve")}
            className="text-green-700 hover:text-green-800 hover:bg-green-50 border-green-200"
          >
            <Eye className="h-4 w-4 mr-1" /> Approve
          </Button>
          {acting && (
            <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
          )}
        </div>
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ ruleFiltered: boolean }> = ({ ruleFiltered }) => (
  <div className="text-center py-16 bg-white border border-gray-200 rounded-lg">
    <Inbox className="h-12 w-12 text-gray-300 mx-auto mb-3" />
    <h3 className="text-lg font-semibold text-gray-700">All caught up</h3>
    <p className="text-sm text-gray-500 mt-1">
      {ruleFiltered
        ? "No hidden comments match this rule filter."
        : "There are no hidden comments waiting for review."}
    </p>
  </div>
);

// -- Helpers ------------------------------------------------------------------

function relativeTime(date: Date): string {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}

export default ReviewQueue;
